import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      console.log('🔍 Checking authentication...');
      // Спробуємо зробити запит до захищеного ендпоінту
      await authAPI.checkAuth();
      console.log('✅ Authentication check successful');
      setIsAuthenticated(true);
    } catch (error: any) {
      console.error('❌ Auth check failed:', error);
      // Если это ошибка "Not authenticated" - это нормально
      if (error.message === 'Not authenticated') {
        console.log('ℹ️ Not authenticated (expected)');
        setIsAuthenticated(false);
      } else if (error.response?.status === 401) {
        // Если получили 401 - сессия истекла, перенаправляем на логин
        console.log('⏰ Session expired, redirecting to login');
        setIsAuthenticated(false);
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
      } else {
        // Другие ошибки (например, сетевые) - тоже считаем не аутентифицированными
        console.error('🌐 Network or other error during auth check:', error);
        setIsAuthenticated(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Перевіряємо автентифікацію при завантаженні, але не на сторінці логіну
    if (location.pathname !== '/login') {
      checkAuth();
    } else {
      // На сторінці логіну просто встановлюємо, що не авторизовані
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, [location.pathname, checkAuth]);

  // Додаткова перевірка при зміні маршруту з логіну на інші сторінки
  useEffect(() => {
    if (location.pathname !== '/login' && !isAuthenticated && !isLoading) {
      checkAuth();
    }
  }, [location.pathname, isAuthenticated, isLoading, checkAuth]);

  const login = async (password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('🔐 Attempting login...');
      setIsLoading(true);
      const response = await authAPI.login(password);
      
      console.log('📥 Login response:', response);
      
      // Проверяем, что сервер вернул success: true
      if (response && response.success === true) {
        console.log('✅ Login successful');
        setIsAuthenticated(true);
        return { success: true };
      } else {
        console.error('❌ Login failed: server returned success: false');
        return { success: false, error: 'Неверный пароль' };
      }
    } catch (error: any) {
      console.error('❌ Login failed:', error);
      
      // Для ошибок авторизации возвращаем специфичное сообщение
      if (error.response?.status === 401) {
        console.log('🔒 Unauthorized - wrong password');
        return { success: false, error: 'Неверный пароль' };
      }
      
      // Для остальных ошибок возвращаем общее сообщение
      // Детальная обработка уже выполнена в глобальном обработчике
      console.log('🌐 Other error during login');
      return { success: false, error: 'Произошла ошибка при входе в систему' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
